import { memo, useState } from "react";
import { AlertTriangle, Camera, CheckCircle2, Circle, Download, Pause, Play, SkipForward, Square, XCircle } from "lucide-react";
import {
  CONDITION_STATE_TEXT,
  HARSHNESS_STYLE,
  LEVEL_STATE_TEXT,
  conditionText,
  currentPhase,
  harshness,
  limitText,
  reasonText,
  soakChecks,
} from "utils/calibrationCampaign";
import { formatDuration } from "utils/planPreview";
import RadarZeroResult from "components/experiment/campaign/RadarZeroResult";

const STATE_STYLE = {
  running: "bg-blue-100 text-blue-800",
  paused: "bg-slate-200 text-slate-700",
  needs_attention: "bg-red-100 text-red-800",
  stopping: "bg-amber-100 text-amber-800",
  complete: "bg-emerald-100 text-emerald-800",
  aborted: "bg-slate-200 text-slate-700",
};
const STATE_TEXT = {
  running: "Running",
  paused: "Paused",
  needs_attention: "Needs attention",
  stopping: "Stopping",
  complete: "Complete",
  aborted: "Stopped",
};
const CONDITION_DOT = {
  done: "bg-emerald-500",
  skipped: "bg-slate-300",
  failed: "bg-red-500",
  pending: "bg-white border border-slate-300",
};

function ConfirmButton({ label, confirmText, onConfirm, icon: Icon, tone = "slate", disabled }) {
  const [asking, setAsking] = useState(false);
  const tones = { slate: "border-slate-200 text-slate-700", red: "border-red-200 text-red-700", blue: "border-blue-200 text-blue-700" };
  if (asking) {
    return (
      <span className="flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900">
        {confirmText}
        <button type="button" className="font-bold text-amber-900 underline" onClick={() => { setAsking(false); onConfirm(); }}>Yes</button>
        <button type="button" className="text-amber-900" onClick={() => setAsking(false)}>No</button>
      </span>
    );
  }
  return (
    <button type="button" disabled={disabled} onClick={() => setAsking(true)} className={`flex items-center gap-1 rounded-xl border bg-white px-3 py-1.5 text-xs font-bold disabled:opacity-40 ${tones[tone]}`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

const HOW = {
  fill: "▲ filled",
  drain: "▼ drained",
  rest: "already there (reading only)",
};

// The condition's planned levels (levels_cm, in order) with what happened at each so far.
function LevelTable({ condition }) {
  const rows = condition.levels || [];
  const planned = condition.levels_cm && condition.levels_cm.length ? condition.levels_cm : rows.map((r) => r.target_cm);
  if (!planned.length) return null;
  const byIndex = Object.fromEntries(rows.map((r) => [r.index, r]));
  return (
    <div className="overflow-x-auto">
      <p className="mb-1 text-xs text-slate-600" data-testid="level-sequence">
        This condition: <strong>{planned.map((l) => `${l} cm`).join(" → ")}</strong>
        {byIndex[0]?.direction === "rest" ? ". The water was already at the first level, so that one is just a reading." : "."}
      </p>
      <table className="w-full text-left text-xs">
        <thead className="text-slate-500">
          <tr>
            <th className="py-1 pr-2">#</th>
            <th className="py-1 pr-2">Water to</th>
            <th className="py-1 pr-2">How</th>
            <th className="py-1 pr-2">Reached</th>
            <th className="py-1 pr-2">Status</th>
            <th className="py-1">Notes</th>
          </tr>
        </thead>
        <tbody>
          {planned.map((target, index) => {
            const level = byIndex[index];
            return (
              <tr key={index} className={`border-t border-slate-100 ${level ? "" : "text-slate-400"}`}>
                <td className="py-1 pr-2">{index + 1}</td>
                <td className="py-1 pr-2">{target} cm</td>
                <td className="py-1 pr-2">{level && level.state !== "measuring" ? HOW[level.direction] || "" : ""}</td>
                <td className="py-1 pr-2">
                  {level && typeof level.arrived_level_cm === "number" ? `${level.arrived_level_cm.toFixed(1)} cm` : "—"}
                  {level?.level_source === "expected" ? " (expected)" : ""}
                </td>
                <td className="py-1 pr-2">{level ? LEVEL_STATE_TEXT[level.state] || level.state : "Next"}</td>
                <td className="py-1 text-slate-500">{level ? (level.flags || []).join(", ").replace(/_/g, " ") : ""}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const every = (seconds) => (seconds < 90 ? `${Math.round(seconds)} s` : formatDuration(seconds));

/**
 * A calibration campaign as it runs (or ran): where it is, what the chamber and the water are doing, why it is
 * waiting if it is, and the controls. Shown on the Experiment tab in place of the designer while it is active.
 */
function CampaignPanel({ campaign, error, pending, onPause, onResume, onStop, onSkip, onExport, onClose, onTestPhoto, isStaff = false, deviceName = "" }) {
  const [photoRequested, setPhotoRequested] = useState(false);
  if (!campaign) return null;
  const conditions = campaign.conditions || [];
  const current = conditions.find((c) => c.seq === campaign.current_seq);
  const active = ["running", "paused", "needs_attention", "stopping"].includes(campaign.state);
  const waiting = campaign.state === "paused" || campaign.state === "needs_attention";
  const checks = current && current.state === "soaking" ? soakChecks(current.soak_stats) : [];
  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="campaign-panel">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-blue-700">Calibration campaign</p>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-lg font-bold text-slate-900">
            {campaign.name}
            <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${STATE_STYLE[campaign.state] || ""}`}>{STATE_TEXT[campaign.state] || campaign.state}</span>
          </p>
          <p className="text-xs text-slate-500">
            Started {new Date(campaign.started_at).toLocaleString()}
            {campaign.created_by ? ` by ${campaign.created_by}` : ""} · sensor {campaign.device_mac}
            {campaign.ended_at ? ` · ended ${new Date(campaign.ended_at).toLocaleString()}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {campaign.state === "running" && !campaign.pause_requested && (
            <ConfirmButton label="Pause" icon={Pause} confirmText="Pause after this condition?" onConfirm={onPause} disabled={pending} />
          )}
          {campaign.state === "running" && current && ["pending", "setting", "soaking"].includes(current.state) && (
            <ConfirmButton label="Skip condition" icon={SkipForward} confirmText="Skip this condition?" onConfirm={onSkip} disabled={pending} />
          )}
          {waiting && (
            <ConfirmButton label="Resume" icon={Play} tone="blue" confirmText="Resume the campaign?" onConfirm={onResume} disabled={pending} />
          )}
          {active && (
            <ConfirmButton label="Stop" icon={Square} tone="red" confirmText="Stop the campaign (the chamber goes to 25 °C / 60 %)?" onConfirm={onStop} disabled={pending} />
          )}
          <button type="button" onClick={onExport} className="flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700">
            <Download className="h-3.5 w-3.5" /> Export
          </button>
          {!active && onClose && (
            <button type="button" onClick={onClose} className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700">
              Close
            </button>
          )}
        </div>
      </div>

      {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-2 text-sm text-red-800">{error}</p>}
      {(waiting || campaign.state === "aborted") && campaign.reason && (
        <div className={`flex items-start gap-2 rounded-xl border p-3 text-sm ${campaign.state === "needs_attention" ? "border-red-200 bg-red-50 text-red-800" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{reasonText(campaign.reason, campaign.detail)}{waiting ? " The chamber is stopped safely; Resume continues from this condition." : ""}</span>
          {waiting && onTestPhoto && (campaign.detail || "").includes("before photo") && !(campaign.detail || "").includes("false alarm") && (
            <button
              type="button"
              data-testid="campaign-test-photo"
              disabled={photoRequested}
              onClick={async () => {
                setPhotoRequested(true);
                try {
                  await onTestPhoto();
                } finally {
                  setTimeout(() => setPhotoRequested(false), 15000);
                }
              }}
              className="ml-auto flex shrink-0 items-center gap-1 rounded-xl border border-red-300 bg-white px-3 py-1.5 text-xs font-bold text-red-800 disabled:opacity-50"
            >
              <Camera className="h-3.5 w-3.5" /> {photoRequested ? "Photo requested…" : "Take test photo"}
            </button>
          )}
        </div>
      )}
      {campaign.pause_requested && <p className="text-xs text-slate-500">Pausing after this condition.</p>}
      {campaign.state === "complete" && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="campaign-complete">
          <strong>Finished.</strong> All {conditions.length} condition{conditions.length === 1 ? "" : "s"} done,{" "}
          {conditions.reduce((n, c) => n + (c.levels || []).filter((l) => l.state === "done").length, 0)} water levels
          measured. The chamber was returned to idle.
        </div>
      )}
      {!active && <RadarZeroResult campaignId={campaign.id} deviceName={deviceName} isStaff={isStaff} />}

      <ol className="flex flex-wrap gap-2" aria-label="Conditions">
        {conditions.map((c) => {
          const level = harshness(c);
          const isCurrent = active && c.seq === campaign.current_seq;
          return (
            <li key={c.id} className={`flex items-center gap-2 rounded-xl border px-2.5 py-1.5 text-xs ${isCurrent ? "border-blue-600 bg-blue-50" : "border-slate-200"}`}>
              <span className={`h-2.5 w-2.5 rounded-full ${isCurrent ? "animate-pulse bg-blue-600" : CONDITION_DOT[c.state] || "bg-amber-400"}`} />
              <span className="font-semibold text-slate-800">{c.seq + 1}. {conditionText(c)}</span>
              {level && <span className={`rounded-full border px-1.5 text-[10px] ${HARSHNESS_STYLE[level]}`}>{level}</span>}
              <span className="text-slate-500">{CONDITION_STATE_TEXT[c.state] || c.state}{c.reason && c.state !== "done" ? ` (${reasonText(c.reason)})` : ""}</span>
              {limitText(c) && <span className="text-amber-700">{limitText(c)}</span>}
            </li>
          );
        })}
      </ol>

      {(campaign.state === "running" || campaign.state === "stopping") && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="rounded-xl bg-slate-50 p-3 md:col-span-2">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Now</p>
            <p className="mt-1 text-sm font-semibold text-slate-900" data-testid="campaign-phase">{currentPhase(campaign)}</p>
            {campaign.sensor_interval_s ? (
              <p className="mt-1 text-xs text-slate-500" data-testid="campaign-wait">
                The sensor reports every {every(campaign.sensor_interval_s)}, so each level waits about{" "}
                {formatDuration(campaign.level_wait_s)} ({campaign.readings_per_level || 3} readings) before moving on.
              </p>
            ) : null}
            {campaign.chamber && (
              <p className="mt-1 text-xs text-slate-500">
                Chamber {campaign.chamber.temp_c ?? "—"} °C / {campaign.chamber.rh_pct ?? "—"} %RH
                {campaign.chamber.target_temp_c != null ? ` (target ${campaign.chamber.target_temp_c} °C / ${campaign.chamber.target_rh_pct} %RH)` : ""}
              </p>
            )}
            {checks.length > 0 && (
              <ul className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-2">
                {checks.map((check) => (
                  <li key={check.key} className="flex items-center gap-1.5 text-xs">
                    {check.ok === true ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : check.ok === false ? <XCircle className="h-3.5 w-3.5 text-slate-400" /> : <Circle className="h-3.5 w-3.5 text-slate-300" />}
                    <span className={check.ok === false ? "text-slate-500" : "text-slate-700"}>{check.label}{check.ok === null ? " (sensor reports none)" : ""}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="rounded-xl bg-slate-50 p-3">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">Left (typical)</p>
            <p className="mt-1 text-sm font-semibold text-slate-900">{formatDuration(campaign.remaining_s)}</p>
            <p className="mt-1 text-xs text-slate-500">
              Water at {typeof campaign.known_level_cm === "number" ? `${campaign.known_level_cm.toFixed(1)} cm` : "—"}
              {campaign.known_level_source && campaign.known_level_source !== "photo" ? ` (${campaign.known_level_source})` : ""} · pump factor {campaign.pump_factor?.toFixed?.(2)}
            </p>
          </div>
        </div>
      )}
      {current && current.levels?.length > 0 && <LevelTable condition={current} />}
    </div>
  );
}

export default memo(CampaignPanel);
